import { useEffect, useState } from "react";
import { Eye, Save, X } from "lucide-react";
import { api } from "@/lib/api";

const EMPTY = { title: "", category: "blog", excerpt: "", cover_image: "", youtube_url: "", content: "", published: true };
const inputCls = "w-full rounded-md border border-line bg-black/40 px-4 py-3 text-sm text-cream placeholder:text-ash/60 focus:border-ember focus:outline-none";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-ash";

export const PostEditor = ({ post, onSaved, onCancel }) => {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    setForm(post ? { title: post.title, category: post.category, excerpt: post.excerpt, cover_image: post.cover_image, youtube_url: post.youtube_url, content: post.content, published: post.published } : EMPTY);
    setError("");
    setPreview(false);
  }, [post]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const saved = post?.id
        ? await api(`/api/posts/${post.id}`, { method: "PUT", body: form, auth: true })
        : await api("/api/posts", { method: "POST", body: form, auth: true });
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-md border border-line bg-card2 p-6 md:p-8" data-testid="post-editor">
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-display text-xl font-bold text-cream">{post?.id ? "Edit post" : "New post"}</h2>
        {post && (
          <button type="button" onClick={onCancel} data-testid="post-editor-cancel" className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ash hover:text-cream">
            <X className="h-4 w-4" /> Cancel
          </button>
        )}
      </div>

      <div className="mt-6 grid gap-5 md:grid-cols-[1fr_180px]">
        <div>
          <label className={labelCls} htmlFor="post-title">Title</label>
          <input id="post-title" required minLength={3} maxLength={200} value={form.title} onChange={set("title")} className={`mt-2 ${inputCls}`} placeholder="Today's headline or note" data-testid="post-title-input" />
        </div>
        <div>
          <label className={labelCls} htmlFor="post-category">Category</label>
          <select id="post-category" value={form.category} onChange={set("category")} className={`mt-2 ${inputCls}`} data-testid="post-category-select">
            <option value="blog">Blog</option>
            <option value="news">News</option>
          </select>
        </div>
      </div>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="post-cover">Cover image URL (optional)</label>
          <input id="post-cover" type="url" value={form.cover_image} onChange={set("cover_image")} className={`mt-2 ${inputCls}`} placeholder="https://…/photo.jpg" data-testid="post-cover-input" />
        </div>
        <div>
          <label className={labelCls} htmlFor="post-youtube">YouTube link (optional)</label>
          <input id="post-youtube" type="url" value={form.youtube_url} onChange={set("youtube_url")} className={`mt-2 ${inputCls}`} placeholder="https://youtu.be/…" data-testid="post-youtube-input" />
        </div>
      </div>

      <div className="mt-5">
        <label className={labelCls} htmlFor="post-excerpt">Short summary (optional — auto-generated if empty)</label>
        <input id="post-excerpt" maxLength={400} value={form.excerpt} onChange={set("excerpt")} className={`mt-2 ${inputCls}`} data-testid="post-excerpt-input" />
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <label className={labelCls} htmlFor="post-content">Text — Markdown: ## heading · **bold** · - list · [link](url)</label>
          <button type="button" onClick={() => setPreview((p) => !p)} data-testid="post-preview-toggle" className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ember hover:text-emberHover">
            <Eye className="h-4 w-4" /> {preview ? "Edit" : "Preview"}
          </button>
        </div>
        {preview ? (
          <div className="prose-editorial mt-2 min-h-[320px] rounded-md border border-line bg-black/40 p-5" data-testid="post-preview">
            <MarkdownPreview text={form.content} />
          </div>
        ) : (
          <textarea id="post-content" required rows={14} value={form.content} onChange={set("content")} className={`mt-2 min-h-[320px] font-mono text-[13px] leading-relaxed ${inputCls}`} placeholder={"## Heading\n\nWrite your post in Telugu or English…"} data-testid="post-content-input" />
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <label className="flex cursor-pointer items-center gap-3 text-sm text-cream">
          <input type="checkbox" checked={form.published} onChange={set("published")} className="h-4 w-4 accent-[#F15A24]" data-testid="post-published-checkbox" />
          Published (visible to everyone)
        </label>
        <button type="submit" disabled={busy} data-testid="post-save-button" className="flex min-h-11 items-center gap-2 rounded-full bg-ember px-6 py-3 text-xs font-bold uppercase tracking-wider text-ink transition-colors duration-300 hover:bg-emberHover disabled:opacity-60">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : post?.id ? "Save changes" : "Publish post"}
        </button>
      </div>
      {error && <p className="mt-4 text-sm text-ember" role="alert" data-testid="post-editor-error">{error}</p>}
    </form>
  );
};

// Lightweight client preview (headings, bold, italics, lists, links, paragraphs); the server renders the final HTML.
const MarkdownPreview = ({ text }) => {
  const lines = text.split("\n");
  const out = [];
  let list = null;
  const inline = (s) =>
    s
      .split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g)
      .filter(Boolean)
      .map((part, i) => {
        if (part.startsWith("**")) return <strong key={i}>{part.slice(2, -2)}</strong>;
        if (part.startsWith("*")) return <em key={i}>{part.slice(1, -1)}</em>;
        const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (m) return <a key={i} href={m[2]} target="_blank" rel="noopener noreferrer">{m[1]}</a>;
        return part;
      });
  const flush = () => {
    if (list) out.push(<ul key={`ul-${out.length}`}>{list}</ul>);
    list = null;
  };
  lines.forEach((line, i) => {
    const h = line.match(/^(#{1,3})\s+(.*)/);
    const li = line.match(/^\s*[-*]\s+(.*)/);
    if (li) {
      list = [...(list || []), <li key={i}>{inline(li[1])}</li>];
      return;
    }
    flush();
    if (h) {
      const Tag = `h${h[1].length + 1}`;
      out.push(<Tag key={i}>{inline(h[2])}</Tag>);
    } else if (line.trim()) out.push(<p key={i}>{inline(line)}</p>);
  });
  flush();
  return out.length ? out : <p className="text-ash">Nothing to preview yet.</p>;
};
